import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { PageHeader, Table, Btn, Modal, Textarea } from "@/components/UI";
import { Plus, FileText, Trash2, Pencil, Send, Mail, Copy, ShoppingCart, RefreshCw, History, X, Link2, AlertTriangle, Globe, Download, Printer } from "lucide-react";
import SmartSuggestInput from "@/components/SmartSuggestInput";
import { usePagination, ListPagination } from "@/components/ListPagination";

// iter331 → iter331.3 — Proforma Fatura modülü.
// Marka + Proje No ayrı kolon YOK (ürün adı altında küçük metadata).
// Toplamlar TEK bloğa düşürüldü (total_currency ile). PDF TR/EN dil seçimli.

const CURRENCIES = ["EUR", "USD", "TRY"];
const AUTO_VALIDITY = "Bu proforma, düzenlenme tarihinden itibaren 15 gün süreyle geçerlidir.";
const AUTO_PAYMENT = "Ödemenin %50'si sipariş sırasında, kalan %50'si ürün tesliminde tahsil edilecektir.";

const emptyLine = () => ({
  product_name: "", brand: "", product_code: "", project_no: "",
  qty: 1, unit_price: "", currency: "EUR", description: "",
  otv: false, kdv: false,
  source_type: null, source_id: null,
  price_mode: "LINKED",
});
const todayIso = () => new Date().toISOString().slice(0, 10);
const emptyProforma = () => ({
  date: todayIso(), customer_id: "", customer_name: "", customer_address: "",
  lines: [emptyLine()], currency: "EUR", total_currency: "TRY",
  notes: "",
  fx_rate_manual: null, revision_note: "",
});

const fmtMoney = (v, cur = "") => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v); if (isNaN(n)) return "";
  const s = n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${s} ${cur}` : s;
};

// iter331.3 — Cross-rate conversion (frontend preview). fx_rates: {cur: rate_to_TRY}
const convertAmount = (amount, from, to, fx) => {
  from = (from || "TRY").toUpperCase(); to = (to || "TRY").toUpperCase();
  if (from === to) return Number(amount) || 0;
  const fr = from === "TRY" ? 1 : Number(fx[from] || 0);
  const tr = to === "TRY" ? 1 : Number(fx[to] || 0);
  const n = (Number(amount) || 0) * fr;
  return tr === 0 ? 0 : n / tr;
};

export default function ProformaPage() {
  const [rows, setRows] = useState([]);
  const [sentIds, setSentIds] = useState(new Set());
  const [ineligMap, setIneligMap] = useState({});
  const [customers, setCustomers] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [rdSamples, setRdSamples] = useState([]);
  const [brands, setBrands] = useState([]);
  const [liveFx, setLiveFx] = useState({}); // iter331.4 — Canlı TCMB fallback
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [p, setP] = useState(emptyProforma());
  const [sending, setSending] = useState({});
  const [revModal, setRevModal] = useState(null);
  const [soWarnModal, setSoWarnModal] = useState(null);
  const [summaryModal, setSummaryModal] = useState(null);
  const [sentFilter, setSentFilter] = useState("all");
  const [soFilter, setSoFilter] = useState("all");
  const [pdfPreview, setPdfPreview] = useState(null); // {pid, revNo?, lang, url}

  const load = useCallback(() =>
    Promise.all([
      api.get("/proformas"), api.get("/customers"),
      api.get("/formulas"), api.get("/rd-samples"),
      api.get("/brands").catch(() => []),
    ]).then(async ([ps, c, f, r, b]) => {
      setRows(ps); setCustomers(c); setFormulas(f); setRdSamples(r); setBrands(b || []);
      try {
        const histResults = await Promise.all(ps.map(x =>
          api.get(`/proformas/${x.id}/send-history`).catch(() => [])));
        const ns = new Set();
        histResults.forEach((h, i) => { if ((h || []).some(e => e.status === "ok")) ns.add(ps[i].id); });
        setSentIds(ns);
      } catch {}
      try {
        const elig = await Promise.all(ps.map(x =>
          api.get(`/proformas/${x.id}/eligibility`).catch(() => null)));
        const m = {};
        elig.forEach((e, i) => { if (e) m[ps[i].id] = e; });
        setIneligMap(m);
      } catch {}
    }),
  []);
  const filtered = useMemo(() => rows.filter(r => {
    if (sentFilter === "sent" && !sentIds.has(r.id)) return false;
    if (sentFilter === "not_sent" && sentIds.has(r.id)) return false;
    if (soFilter === "has_so" && !r.sales_order_id) return false;
    if (soFilter === "no_so" && r.sales_order_id) return false;
    return true;
  }), [rows, sentIds, sentFilter, soFilter]);
  const pg = usePagination(filtered, "proformas", 100);
  useEffect(() => { load(); }, [load]);

  // iter331.4 — TCMB kurlarını yeni proforma hesaplaması için önden yükle.
  // editing yoksa cross-rate dönüşümünde fallback olarak kullanılır.
  useEffect(() => {
    api.get("/fx/rates").then(r => {
      const rates = (r && r.rates) || {};
      setLiveFx({ ...rates, TRY: 1.0 });
    }).catch(() => {});
  }, []);

  const lineTotals = (l) => {
    const qty = Number(l.qty) || 0;
    const up = l.unit_price === "" || l.unit_price === null ? 0 : Number(l.unit_price);
    const sub = qty * up;
    const otv = l.otv ? sub * 0.20 : 0;
    const kdv = l.kdv ? (sub + otv) * 0.20 : 0;
    return { sub, otv, kdv, total: sub + otv + kdv };
  };

  // iter331.3/4 — Tek toplam, hedef currency ile cross-rate hesabı.
  // Yeni proformada editing yok → liveFx (TCMB) kullanılır.
  const fxLocal = useMemo(() => ({ ...liveFx, ...(editing?.fx_rates || {}) }), [liveFx, editing]);
  const grandTarget = useMemo(() => {
    const target = (p.total_currency || "TRY").toUpperCase();
    let sub = 0, otv = 0, kdv = 0, total = 0;
    for (const l of p.lines || []) {
      const cur = (l.currency || "EUR").toUpperCase();
      const t = lineTotals(l);
      sub += convertAmount(t.sub, cur, target, fxLocal);
      otv += convertAmount(t.otv, cur, target, fxLocal);
      kdv += convertAmount(t.kdv, cur, target, fxLocal);
      total += convertAmount(t.total, cur, target, fxLocal);
    }
    return { subtotal: sub, otv, kdv, total };
  }, [p.lines, p.total_currency, fxLocal]);
  const anyOtv = useMemo(() => (p.lines || []).some(l => l.otv), [p.lines]);
  const usedCurrencies = useMemo(() => {
    const s = new Set();
    for (const l of p.lines || []) s.add((l.currency || "EUR").toUpperCase());
    return [...s];
  }, [p.lines]);

  const openNew = () => { setEditing(null); setP(emptyProforma()); setOpen(true); };
  const openEdit = async (r) => {
    const d = await api.get(`/proformas/${r.id}`);
    setEditing(d);
    setP({ ...emptyProforma(), ...d,
      total_currency: (d.total_currency || "TRY").toUpperCase(),
      lines: (d.lines?.length ? d.lines.map(l => ({ ...emptyLine(), ...l, currency: (l.currency || d.currency || "EUR").toUpperCase() })) : [emptyLine()]),
      notes: d.notes || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!p.customer_name) { alert("Müşteri adı zorunludur"); return; }
    try {
      if (editing) await api.put(`/proformas/${editing.id}`, p);
      else await api.post("/proformas", p);
      setOpen(false); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const del = async (id) => {
    if (!window.confirm("Bu Proforma arşivlensin mi?")) return;
    try { await api.del(`/proformas/${id}`); load(); } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const copyPf = async (r) => {
    try { await api.post(`/proformas/${r.id}/copy`, {}); load(); alert("Kopya oluşturuldu."); }
    catch (e) { alert(e.response?.data?.detail || "Kopya başarısız"); }
  };

  // iter331.3 — PDF Preview (TR default, EN toggle via inline modal)
  const openPdfModal = async (r, lang = "tr", revNo = null) => {
    try {
      const path = revNo != null
        ? `/proformas/${r.id}/revisions/${revNo}/pdf?lang=${lang}`
        : `/proformas/${r.id}/pdf?lang=${lang}`;
      const blob = await api.getBlob(path);
      const url = URL.createObjectURL(blob);
      setPdfPreview({ proforma: r, revNo, lang, url, blob });
    } catch { alert("PDF alınamadı"); }
  };
  const switchPdfLang = async (newLang) => {
    if (!pdfPreview) return;
    URL.revokeObjectURL(pdfPreview.url);
    await openPdfModal(pdfPreview.proforma, newLang, pdfPreview.revNo);
  };
  const downloadPdfBlob = () => {
    if (!pdfPreview) return;
    const a = document.createElement("a");
    a.href = pdfPreview.url;
    a.download = `Proforma_${pdfPreview.proforma.pf_no}_${pdfPreview.lang.toUpperCase()}.pdf`;
    a.click();
  };

  const sendTg = async (r) => {
    setSending(s => ({ ...s, [r.id]: "tg" }));
    try { await api.post(`/proformas/${r.id}/send-telegram`, {}); alert(`✓ Telegram gönderildi: ${r.pf_no}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Telegram gönderimi başarısız."); }
    finally { setSending(s => ({ ...s, [r.id]: null })); }
  };
  const sendMail = async (r) => {
    setSending(s => ({ ...s, [r.id]: "mail" }));
    try { await api.post(`/proformas/${r.id}/send-email`, {}); alert(`✓ E-posta gönderildi: ${r.pf_no}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "E-posta gönderilemedi."); }
    finally { setSending(s => ({ ...s, [r.id]: null })); }
  };
  const updateFx = async (pfId) => {
    try {
      await api.post(`/proformas/${pfId}/update-fx`, { manual_rate: null });
      alert("Kurlar güncellendi (yeni revizyon oluşturuldu).");
      if (editing && editing.id === pfId) {
        const d = await api.get(`/proformas/${pfId}`);
        setEditing(d); setP(prev => ({ ...prev, ...d, lines: prev.lines }));
      }
      load();
    } catch (e) { alert(e.response?.data?.detail || "Kur güncellenemedi."); }
  };
  const createSO = async (r, confirm = false) => {
    try {
      const resp = await api.post(`/proformas/${r.id}/create-sales-order`, { confirm_registered_only: confirm });
      if (resp.requires_confirm) { setSoWarnModal({ proforma: r, ineligible: resp.ineligible || [] }); return; }
      load();
      const skipped = resp.skipped_ineligible || [];
      alert(`✓ Satış Siparişi oluşturuldu: ${resp.sales_order?.order_no}${skipped.length ? `\n⚠ ${skipped.length} kalem aktarılmadı.` : ""}`);
      setSoWarnModal(null);
    } catch (e) { alert(e.response?.data?.detail || "Satış Siparişi oluşturulamadı."); }
  };
  const showRevisions = async (r) => {
    try { const revs = await api.get(`/proformas/${r.id}/revisions`); setRevModal({ proforma: r, revs }); }
    catch (e) { alert(e.response?.data?.detail || "Revizyonlar alınamadı."); }
  };
  const showSummary = async (r) => {
    try {
      const full = await api.get(`/proformas/${r.id}`);
      const history = await api.get(`/proformas/${r.id}/send-history`);
      setSummaryModal({ pf: full, history });
    } catch (e) { alert(e.response?.data?.detail || "Özet alınamadı."); }
  };

  const updateLine = (i, patch) => setP(prev => {
    const l = [...prev.lines]; l[i] = { ...l[i], ...patch }; return { ...prev, lines: l };
  });
  const addLine = () => setP(prev => ({ ...prev, lines: [...prev.lines, { ...emptyLine(), currency: prev.currency || "EUR" }] }));
  const removeLine = (i) => setP(prev => ({ ...prev, lines: prev.lines.filter((_, j) => j !== i) }));

  // iter331.6 — brand_id → brand_name map (formula dokümanı brand_name taşımıyor).
  const brandMap = useMemo(() => {
    const m = {};
    for (const b of brands || []) m[b.id] = b.name;
    return m;
  }, [brands]);

  const productSuggestions = useMemo(() => {
    const items = [];
    for (const f of formulas || []) {
      if (!f.product_name && !f.project_no) continue;
      const bn = f.brand_name || brandMap[f.brand_id] || null;
      items.push({
        id: `F:${f.id}`, source_type: "FORMULA", source_id: f.id,
        product_name: f.product_name, project_no: f.project_no,
        brand_id: f.brand_id, brand_name: bn, sales_price_eur: f.sales_price_eur,
      });
    }
    for (const s of rdSamples || []) {
      if (!s.product_name && !s.project_no) continue;
      const bn = s.brand_name || brandMap[s.brand_id] || null;
      items.push({
        id: `R:${s.id}`, source_type: "RD_PROJECT", source_id: s.id,
        product_name: s.product_name, project_no: s.project_no,
        brand_id: s.brand_id, brand_name: bn,
      });
    }
    return items;
  }, [formulas, rdSamples, brandMap]);

  const onPickProduct = (i, item) => {
    if (!item) return;
    updateLine(i, {
      product_name: item.product_name || "",
      brand: item.brand_name || "",
      project_no: item.project_no || "",
      unit_price: item.sales_price_eur ?? "",
      source_type: item.source_type, source_id: item.source_id, price_mode: "LINKED",
    });
  };

  const onCustomerPick = ({ selectedId, value }) => {
    if (selectedId) {
      const c = customers.find(x => x.id === selectedId);
      if (c) {
        const addr = c.billing_address
          || c.delivery_addresses?.[0]?.full_address
          || c.delivery_addresses?.[0]?.line
          || c.delivery_addresses?.[0]?.line1 || "";
        setP(prev => ({ ...prev, customer_id: selectedId, customer_name: c.legal_name || c.name || value, customer_address: addr }));
      }
    } else {
      setP(prev => ({ ...prev, customer_id: "", customer_name: value || "" }));
    }
  };

  const ineligCount = (r) => (ineligMap[r.id]?.ineligible || []).length;
  const targetCur = (p.total_currency || "TRY").toUpperCase();

  // iter331.3 — Marka + Proje No ayrı kolon YOK, 10 kolon
  const GRID = "3.5fr 1fr 1.4fr 1fr 1.4fr 1.5fr 2fr 36px 36px 32px";

  return (
    <div>
      <PageHeader title="Proforma Fatura"
        description="Müşteriye özel proforma faturalar — PDF, Telegram, E-posta ve Satış Siparişi aktarımı"
        actions={<Btn onClick={openNew} data-testid="new-proforma"><Plus size={14}/> Yeni Proforma</Btn>} />
      <div className="flex flex-wrap gap-2 items-center text-xs mb-2">
        <span className="text-slate-500">Gönderim:</span>
        <select value={sentFilter} onChange={e => setSentFilter(e.target.value)}
                className="px-2 py-1 border rounded text-xs" data-testid="pf-filter-sent">
          <option value="all">Tümü</option><option value="sent">Gönderildi</option><option value="not_sent">Gönderilmedi</option>
        </select>
        <span className="text-slate-500 ml-2">Sipariş:</span>
        <select value={soFilter} onChange={e => setSoFilter(e.target.value)}
                className="px-2 py-1 border rounded text-xs" data-testid="pf-filter-so">
          <option value="all">Tümü</option><option value="has_so">Satış Siparişi Oluştu</option><option value="no_so">Satış Siparişi Oluşmadı</option>
        </select>
        {(sentFilter !== "all" || soFilter !== "all") && (
          <button onClick={() => { setSentFilter("all"); setSoFilter("all"); }}
                  className="text-indigo-600 hover:underline ml-1" data-testid="pf-filter-clear">Filtreleri Temizle</button>
        )}
        <span className="text-slate-400 ml-auto">{filtered.length} / {rows.length} kayıt</span>
      </div>
      <Table columns={[
        { label: "PF No", render: r => (
          <button onClick={() => showSummary(r)} className="font-mono text-xs text-indigo-700 hover:underline flex items-center gap-1" data-testid={`pf-no-${r.id}`}>
            {r.pf_no}
            {ineligCount(r) > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] bg-amber-100 text-amber-800 px-1 rounded"
                    title={`${ineligCount(r)} kalem Satış Siparişine aktarılamaz`}
                    data-testid={`pf-inelig-badge-${r.id}`}>
                <AlertTriangle size={9}/> {ineligCount(r)}
              </span>
            )}
          </button>
        ) },
        { label: "Tarih", render: r => (r.date || "").slice(0, 10) },
        { label: "Müşteri", render: r => r.customer_name || "—" },
        { label: "Rev", render: r => <span className="text-xs font-mono">R{String(r.current_revision || 0).padStart(2, "0")}</span> },
        { label: "Genel Toplam", render: r => <span className="font-mono text-xs">{fmtMoney(r.totals_target?.grand_total ?? r.grand_total_try, (r.totals_target?.currency || "TRY"))}</span> },
        { label: "SS", render: r => r.sales_order_no ? <span className="text-emerald-700 text-xs font-mono" title={`Revision ${r.sales_order_revision}`}>✓ {r.sales_order_no}</span> : <span className="text-slate-300">—</span> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="secondary" onClick={() => openPdfModal(r, "tr")} data-testid={`pf-pdf-${r.id}`} title="PDF Önizle"><FileText size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => sendTg(r)} data-testid={`pf-tg-${r.id}`} title="Telegram" disabled={sending[r.id] === "tg"}><Send size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => sendMail(r)} data-testid={`pf-mail-${r.id}`} title="E-posta" disabled={sending[r.id] === "mail"}><Mail size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => createSO(r, false)} data-testid={`pf-so-${r.id}`} title="Satış Siparişi Oluştur"><ShoppingCart size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => copyPf(r)} data-testid={`pf-copy-${r.id}`} title="Kopyala"><Copy size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`pf-edit-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>
            <Btn size="sm" variant="secondary" onClick={() => showRevisions(r)} data-testid={`pf-rev-${r.id}`} title="Revizyon Geçmişi"><History size={12}/></Btn>
            <Btn size="sm" variant="danger" onClick={() => del(r.id)} data-testid={`pf-del-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>
          </div>
        ) },
      ]} rows={pg.paged} testid="proforma-table"/>
      <ListPagination pagination={pg} testid="proforma-pagination"/>

      {/* ----- Yeni / Düzenle Modal ----- */}
      <Modal open={open} onClose={() => setOpen(false)}
             title={editing ? `Proforma Düzenle — ${editing.pf_no}` : "Yeni Proforma Fatura"} size="full">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="text-xs text-slate-500">Tarih</label>
            <input type="date" value={p.date} onChange={e => setP({ ...p, date: e.target.value })}
                   className="w-full px-2 py-1.5 text-sm border rounded" data-testid="pf-date"/>
          </div>
          <div className="sm:col-span-3">
            <SmartSuggestInput label="Müşteri (kayıtlı seçilebilir veya elle yazılabilir)"
              value={p.customer_name} selectedId={p.customer_id} items={customers}
              displayField="legal_name" idField="id"
              secondaryField={c => [c.code && `Kod: ${c.code}`, c.city].filter(Boolean).join(" · ")}
              placeholder="Müşteri adı yazın veya seçin..."
              onChange={onCustomerPick} data-testid="pf-cust"/>
          </div>
        </div>
        <div className="mb-3">
          <Textarea label="Müşteri Adresi (kayıtlıysa otomatik dolar, elle değiştirilebilir)"
                    rows={2} value={p.customer_address}
                    onChange={e => setP({ ...p, customer_address: e.target.value })}
                    data-testid="pf-cust-address"/>
        </div>

        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Ürün Satırları</div>
            <Btn size="sm" variant="secondary" onClick={addLine} data-testid="pf-add-line"><Plus size={12}/> Satır Ekle</Btn>
          </div>
          <div className="grid gap-2 text-[10px] uppercase text-slate-500 font-semibold px-1 mb-1" style={{ gridTemplateColumns: GRID }}>
            <div>Ürün Adı</div>
            <div className="text-right">Adet</div>
            <div className="text-right">Fiyat</div>
            <div className="text-center">Para Birimi</div>
            <div className="text-right">Satır Ara</div>
            <div className="text-right">Satır Toplam</div>
            <div>Kısa Açıklama</div>
            <div className="text-center">ÖTV</div>
            <div className="text-center">KDV</div>
            <div></div>
          </div>
          {(p.lines || []).map((l, i) => {
            const t = lineTotals(l);
            const lineCur = (l.currency || "EUR").toUpperCase();
            // iter331.3 — Marka + Proje ürün adının altında küçük metadata
            const metaBits = [
              l.brand && `Marka: ${l.brand}`,
              l.project_no && `Proje: ${l.project_no}`,
            ].filter(Boolean);
            return (
              <div key={i} className="grid gap-2 bg-white border border-slate-200 rounded p-1 items-start mb-1" style={{ gridTemplateColumns: GRID }}>
                <div className="flex flex-col gap-0.5">
                  <SmartSuggestInput value={l.product_name} items={productSuggestions}
                    displayField="product_name" idField="id"
                    secondaryField={s => [
                      s.project_no,
                      s.brand_name ? `Marka: ${s.brand_name}` : null,
                      s.source_type === "FORMULA" ? "Formül" : "Ar-Ge",
                    ].filter(Boolean).join(" · ")}
                    placeholder="Ürün adı yaz veya seç..."
                    onChange={({ value, item }) => {
                      if (item) onPickProduct(i, item);
                      else updateLine(i, { product_name: value, source_type: null, source_id: null, price_mode: "MANUAL" });
                    }}
                    data-testid={`pf-line-name-${i}`}/>
                  {metaBits.length > 0 && (
                    <div className="text-[10px] text-slate-500 px-1" data-testid={`pf-line-meta-${i}`}>
                      {metaBits.join(" • ")}
                    </div>
                  )}
                </div>
                <input type="number" step="1" value={l.qty || ""}
                       onChange={e => updateLine(i, { qty: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                       className="w-full px-2 py-1 text-xs border rounded text-right" data-testid={`pf-line-qty-${i}`}/>
                <input type="number" step="0.01" value={l.unit_price === null || l.unit_price === undefined ? "" : l.unit_price}
                       onChange={e => updateLine(i, {
                         unit_price: e.target.value === "" ? "" : parseFloat(e.target.value),
                         price_mode: l.source_id ? "MANUAL" : l.price_mode,
                       })}
                       className="w-full px-2 py-1 text-xs border rounded text-right font-mono" data-testid={`pf-line-price-${i}`}/>
                <select value={lineCur} onChange={e => updateLine(i, { currency: e.target.value })}
                        className="w-full px-1 py-1 text-xs border rounded text-center" data-testid={`pf-line-cur-${i}`}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className="text-xs font-mono text-right px-1" data-testid={`pf-line-sub-${i}`}>{t.sub ? fmtMoney(t.sub, lineCur) : ""}</span>
                <span className="text-xs font-mono text-right px-1 font-semibold text-emerald-700" data-testid={`pf-line-total-${i}`}>{t.total ? fmtMoney(t.total, lineCur) : ""}</span>
                <input value={l.description || ""} onChange={e => updateLine(i, { description: e.target.value })}
                       placeholder="açıklama" className="w-full px-2 py-1 text-xs border rounded" data-testid={`pf-line-desc-${i}`}/>
                <label className="flex justify-center pt-1"><input type="checkbox" checked={!!l.otv} onChange={e => updateLine(i, { otv: e.target.checked })} data-testid={`pf-line-otv-${i}`}/></label>
                <label className="flex justify-center pt-1"><input type="checkbox" checked={!!l.kdv} onChange={e => updateLine(i, { kdv: e.target.checked })} data-testid={`pf-line-kdv-${i}`}/></label>
                <button onClick={() => removeLine(i)} className="text-red-500 text-xs pt-1" title="Satırı sil" data-testid={`pf-line-del-${i}`}><X size={12}/></button>
              </div>
            );
          })}
        </div>

        {/* iter331.3 — TEK toplam paneli, Toplam Para Birimi dropdown ile */}
        <div className="mt-3 bg-white border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Toplam Para Birimi:</label>
              <select value={targetCur} onChange={e => setP({ ...p, total_currency: e.target.value })}
                      className="px-2 py-1 border rounded text-sm" data-testid="pf-total-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {editing && (
              <Btn size="sm" variant="secondary" onClick={() => updateFx(editing.id)} data-testid="pf-update-fx">
                <RefreshCw size={11}/> Kurları Güncelle
              </Btn>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
            <div><div className="text-[10px] text-slate-500">Ara Toplam</div><div className="font-mono font-semibold">{fmtMoney(grandTarget.subtotal, targetCur)}</div></div>
            {anyOtv && <div><div className="text-[10px] text-slate-500">Toplam ÖTV</div><div className="font-mono">{fmtMoney(grandTarget.otv, targetCur)}</div></div>}
            <div><div className="text-[10px] text-slate-500">Toplam KDV</div><div className="font-mono">{fmtMoney(grandTarget.kdv, targetCur)}</div></div>
            <div><div className="text-[10px] text-slate-500">GENEL TOPLAM</div><div className="font-mono text-base font-bold text-emerald-700" data-testid="pf-grand-total">{fmtMoney(grandTarget.total, targetCur)}</div></div>
          </div>
          {usedCurrencies.filter(c => c !== "TRY" || targetCur !== "TRY").length > 0 && (
            <div className="mt-2 text-[11px] text-slate-500 border-t pt-2">
              {[...new Set([...usedCurrencies, targetCur])].filter(c => c !== "TRY").map(c => (
                <div key={c}>1 {c} = {fmtMoney(fxLocal[c])} TRY</div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded p-2 text-xs text-slate-700" data-testid="pf-auto-notes-preview">
          <div className="font-semibold text-indigo-900 mb-1">Otomatik Notlar (PDF'te sabit metinler)</div>
          <div>• {AUTO_VALIDITY}</div>
          <div>• {AUTO_PAYMENT}</div>
        </div>

        <div className="mt-3">
          <Textarea label="Ek Notlar (PDF'te otomatik notların altında görünür)" rows={3} value={p.notes}
                    onChange={e => setP({ ...p, notes: e.target.value })} data-testid="pf-notes"/>
        </div>
        {editing && (
          <div className="mt-3">
            <input className="w-full px-2 py-1.5 text-sm border rounded" placeholder="Revizyon Açıklaması (opsiyonel)"
                   value={p.revision_note || ""}
                   onChange={e => setP({ ...p, revision_note: e.target.value })} data-testid="pf-rev-note"/>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="save-proforma">Kaydet</Btn>
        </div>
      </Modal>

      {/* ----- PDF Preview Modal (TR/EN toggle) ----- */}
      <Modal open={!!pdfPreview} onClose={() => { if (pdfPreview) URL.revokeObjectURL(pdfPreview.url); setPdfPreview(null); }}
             title={`PDF Önizleme — ${pdfPreview?.proforma?.pf_no || ""}`} size="full">
        {pdfPreview && (
          <div className="flex flex-col h-[75vh]">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-slate-500">
                Dil: <b>{pdfPreview.lang === "en" ? "English" : "Türkçe"}</b>
              </div>
              <div className="flex gap-2">
                {pdfPreview.lang === "tr" ? (
                  <Btn size="sm" variant="secondary" onClick={() => switchPdfLang("en")} data-testid="pf-pdf-lang-en">
                    <Globe size={11}/> İngilizceye Çevir
                  </Btn>
                ) : (
                  <Btn size="sm" variant="secondary" onClick={() => switchPdfLang("tr")} data-testid="pf-pdf-lang-tr">
                    <Globe size={11}/> Türkçeye Dön
                  </Btn>
                )}
                <Btn size="sm" variant="secondary" onClick={downloadPdfBlob} data-testid="pf-pdf-download"><Download size={11}/> İndir</Btn>
                <Btn size="sm" variant="secondary" onClick={() => window.open(pdfPreview.url, "_blank")} data-testid="pf-pdf-print"><Printer size={11}/> Yazdır</Btn>
              </div>
            </div>
            <iframe title="PDF Preview" src={pdfPreview.url} className="flex-1 border rounded" data-testid="pf-pdf-iframe"/>
          </div>
        )}
      </Modal>

      {/* ----- Revisions Modal ----- */}
      <Modal open={!!revModal} onClose={() => setRevModal(null)}
             title={`Revizyon Geçmişi — ${revModal?.proforma?.pf_no || ""}`} size="xl">
        {revModal && (
          <div className="space-y-2">
            {revModal.revs.map(rv => (
              <div key={rv.id} className="border border-slate-200 rounded p-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">R{String(rv.revision_no).padStart(2, "0")} — {rv.change_summary}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500">{(rv.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <Btn size="sm" variant="secondary" onClick={() => openPdfModal(revModal.proforma, "tr", rv.revision_no)} title="Bu revizyonun PDF'ini aç" data-testid={`pf-rev-pdf-${rv.revision_no}`}><FileText size={11}/></Btn>
                  </div>
                </div>
                {rv.manual_note && <div className="text-xs text-slate-600 mt-1 italic">📝 {rv.manual_note}</div>}
                {(rv.diff || []).length > 0 && (
                  <div className="mt-1 text-[11px] font-mono bg-slate-50 border border-slate-200 rounded p-1">
                    {rv.diff.map((d, i) => (
                      <div key={i}>• {d.field}: <span className="text-red-600">{String(d.from ?? "—")}</span> → <span className="text-emerald-700">{String(d.to ?? "—")}</span></div>
                    ))}
                  </div>
                )}
                {rv.change_source === "SOURCE_UPDATE" && <span className="inline-block mt-1 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Kaynak Güncellemesi</span>}
                {rv.change_source === "FX_UPDATE" && <span className="inline-block mt-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Kur Güncellemesi</span>}
              </div>
            ))}
            <div className="pt-2 border-t">
              <Btn size="sm" variant="secondary" onClick={() => updateFx(revModal.proforma.id)} data-testid="pf-rev-update-fx"><RefreshCw size={11}/> Kurları Güncelle (TCMB)</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ----- SO Warning Modal ----- */}
      <Modal open={!!soWarnModal} onClose={() => setSoWarnModal(null)}
             title="Satış Siparişine Aktarılmayacak Ürünler" size="md">
        {soWarnModal && (
          <div className="space-y-3">
            <div className="text-xs text-slate-600">Yalnızca Formüller sayfasındaki ONAYLI ürünler Satış Siparişine otomatik aktarılacaktır.</div>
            <ul className="text-xs bg-amber-50 border border-amber-200 rounded p-2 space-y-1" data-testid="pf-so-warn-list">
              {soWarnModal.ineligible.map((it, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <AlertTriangle size={11} className="text-amber-600 mt-0.5 flex-shrink-0"/>
                  <div><div className="font-semibold">{it.product_name}</div><div className="text-slate-600">{it.reason}</div></div>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setSoWarnModal(null)} data-testid="pf-so-cancel">İptal</Btn>
              <Btn onClick={() => createSO(soWarnModal.proforma, true)} data-testid="pf-so-confirm">Onaylı Formüllerle Devam Et</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ----- Summary Modal ----- */}
      <Modal open={!!summaryModal} onClose={() => setSummaryModal(null)}
             title={summaryModal?.pf ? `${summaryModal.pf.pf_no} — ${summaryModal.pf.customer_name || ""}` : ""} size="xl">
        {summaryModal?.pf && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><div className="text-[10px] text-slate-500">Tarih</div>{summaryModal.pf.date}</div>
              <div><div className="text-[10px] text-slate-500">Müşteri</div>{summaryModal.pf.customer_name}</div>
              <div><div className="text-[10px] text-slate-500">Aktif Revizyon</div>R{String(summaryModal.pf.current_revision || 0).padStart(2, "0")}</div>
              <div><div className="text-[10px] text-slate-500">GENEL TOPLAM</div><span className="font-semibold">{fmtMoney(summaryModal.pf.totals_target?.grand_total, summaryModal.pf.totals_target?.currency || "TRY")}</span></div>
            </div>
            <div><div className="text-[10px] text-slate-500">Adres</div><div className="text-xs">{summaryModal.pf.customer_address || "—"}</div></div>
            {Object.entries(summaryModal.pf.fx_rates || {}).filter(([c]) => c !== "TRY").length > 0 && (
              <div className="text-xs text-slate-600 border-t pt-2">
                {Object.entries(summaryModal.pf.fx_rates || {}).filter(([c]) => c !== "TRY").sort(([a],[b]) => a.localeCompare(b)).map(([c, r]) => (
                  <div key={c}>1 {c} = {fmtMoney(r)} TRY</div>
                ))}
              </div>
            )}
            {(ineligMap[summaryModal.pf.id]?.ineligible || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs" data-testid="pf-summary-inelig">
                <div className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={12}/> Satış Siparişine Otomatik Aktarılmayacak Kalemler</div>
                <ul className="pl-1 space-y-0.5 mt-1">
                  {(ineligMap[summaryModal.pf.id].ineligible).map((it, i) => (
                    <li key={i}>• <b>{it.product_name}</b> — <span className="text-slate-600">{it.reason}</span></li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <div className="text-[10px] text-slate-500 mb-1">Ürün Satırları</div>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500">
                  <tr><th className="text-left">Ürün</th><th className="text-right">Adet</th><th className="text-right">Birim Fiyat</th><th className="text-right">Toplam</th></tr>
                </thead>
                <tbody>
                  {(summaryModal.pf.lines || []).map((l, i) => {
                    const meta = [l.brand && `Marka: ${l.brand}`, l.project_no && `Proje: ${l.project_no}`].filter(Boolean).join(" • ");
                    const lineCur = (l.currency || summaryModal.pf.currency || "").toUpperCase();
                    return (
                      <tr key={i} className="border-t">
                        <td><div>{l.product_name || "—"}</div>{meta && <div className="text-[10px] text-slate-500">{meta}</div>}</td>
                        <td className="text-right font-mono">{l.qty || ""}</td>
                        <td className="text-right font-mono">{l.unit_price === null || l.unit_price === "" ? "" : fmtMoney(l.unit_price, lineCur)}</td>
                        <td className="text-right font-mono">{l.line_total ? fmtMoney(l.line_total, lineCur) : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {summaryModal.pf.sales_order_no && (
              <div className="text-xs">
                <Link2 size={12} className="inline"/> Bağlı Satış Siparişi: <span className="font-mono text-emerald-700">{summaryModal.pf.sales_order_no}</span>
                {" — Proforma R" + String(summaryModal.pf.sales_order_revision || 0).padStart(2, "0") + " üzerinden oluşturulmuştur."}
              </div>
            )}
            {(summaryModal.history || []).length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 mb-1">Gönderim Geçmişi</div>
                <ul className="text-xs space-y-0.5">
                  {summaryModal.history.map(h => (
                    <li key={h.id}>R{String(h.revision_no).padStart(2, "0")} · {(h.sent_at || "").slice(0, 16).replace("T", " ")} · {h.channel} · {h.recipient || ""}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
